import {
  DataNode,
  Device,
  DeviceAux,
  DeviceData,
  DeviceDataGroup,
  DeviceEutGroup,
  DeviceTestData,
  DeviceTestEquipment,
  Employee,
  Examination,
  NodeDataType,
  Report,
  ResultType,
} from "graphql/types"
import { createCaption } from "utils/createCaption"
import { getNormName } from "utils/getNormName"
import { getMaxValuesByUnit } from "utils/parseNumberAndUnit"
import { formatMissingValue, formatReportDate } from "utils/reportFormatting"
import { getResultTypeNameForReport } from "utils/resultTypes"
import { resultTasks, setUpTasks } from "utils/taskSteps"
import { v4 } from "uuid"
import { isDeviceAux, isDeviceEut, isDeviceTe, isNodeTable } from "./typeGuards"

const createTableNumber = () => {
  return 1
}

const createImageNumbers = (count: number) => {
  return Array.from({ length: count }, (_, i) => i + 1)
}

const emptyDataNode: DataNode = {
  dataType: NodeDataType.TEXT,
  id: "",
  text: "",
  editable: true,
  deletable: true,
  image: [],
  imageCaption: [],
  imageNumber: [],
  tableBody: [],
  tableCaption: "",
  tableHeader: [],
  tableNumber: 0,
  isAutoGenerated: false,
  sourceExaminationId: undefined,
}

// Ensure unique DataNode IDs to avoid duplicate React keys
const ensureUniqueNodeIds = (nodes: DataNode[]): DataNode[] => {
  const seen = new Set<string>()
  return nodes.map((node) => {
    let newId = node.id
    if (!newId || seen.has(newId)) {
      newId = v4()
    }
    seen.add(newId)
    return { ...node, id: newId }
  })
}

const getTestResultsForTable = (dtd: DeviceTestData) => {
  if (dtd.result.some((item) => item.type === ResultType.NOT_EVALUATED)) {
    return {
      barcode: dtd.device.barcode,
      result: getResultTypeNameForReport(ResultType.NOT_EVALUATED),
    }
  }
  if (dtd.result.some((item) => item.type === ResultType.FAILED)) {
    return {
      barcode: dtd.device.barcode,
      result: getResultTypeNameForReport(ResultType.FAILED),
    }
  }

  return { barcode: dtd.device.barcode, result: getResultTypeNameForReport(ResultType.PASSED) }
}

// Create 2.0 TestOverview Chapter
export const getTestOverviewData = (
  examination: Examination,
  oldData: DataNode[],
  report: Report,
  eutGroups: DeviceEutGroup[],
  allExaminations: Examination[],
  employees: Employee[]
): DataNode[] => {
  const getTestResultsForOverview = () => {
    const datas = examination.tasks
      .flatMap((task) => task.testDatas)
      .filter((test) => resultTasks.includes(test.type))

    const deviceTestData = datas.flatMap((item) => item.deviceTestData)

    const returnValue = deviceTestData.map((dtd) => {
      return getTestResultsForTable(dtd)
    })
    return returnValue
  }

  const oldTableBody = oldData.length > 0 && isNodeTable(oldData[0]) ? oldData[0].tableBody : []

  const section = 3 + oldTableBody.length

  // Determine responsible tester: prefer examination.tester, fallback to report.tester[0]

  const tester = employees?.find((employee) => employee.id === examination.tester?.id)

  const examTesterName = (() => {
    const t = tester as any
    if (!t) return null
    // Prefer EmployeeName, fallback to ContactInfo First/Last
    // if (t.EmployeeName) return t.EmployeeName as string
    const first = t.ContactInfo?.FirstName || ""
    const last = t.ContactInfo?.LastName || ""
    const full = `${first} ${last}`.trim()
    return full || null
  })()

  const testerName =
    examTesterName ||
    (report?.tester && report.tester.length > 0
      ? `${report.tester[0].firstName} ${report.tester[0].lastName}`.trim()
      : formatMissingValue(null))

  // Get actual test dates from examination timestamps or fall back to task data
  // Try to get the earliest start date and latest end date from all tasks
  let testStartDate = examination.startTimestamp || null
  let testEndDate = examination.endTimestamp || null

  // If no examination timestamps, look through all tasks for dates
  if (!testStartDate || !testEndDate) {
    const allBeginTimes: string[] = []
    const allEndTimes: string[] = []

    examination.tasks?.forEach((task) => {
      task.testDatas?.forEach((td) => {
        if (td.beginTime) allBeginTimes.push(td.beginTime)
        if (td.endTime) allEndTimes.push(td.endTime)
      })
    })

    // Get earliest start and latest end
    if (!testStartDate && allBeginTimes.length > 0) {
      testStartDate = allBeginTimes.sort()[0]
    }
    if (!testEndDate && allEndTimes.length > 0) {
      testEndDate = allEndTimes.sort().reverse()[0]
    }
  }

  // Format as date range if we have both dates, otherwise single date or placeholder
  let testDateDisplay = formatMissingValue(null)
  if (testStartDate && testEndDate) {
    // Check if both dates are on the same day
    const startDate = new Date(testStartDate)
    const endDate = new Date(testEndDate)
    const isSameDay = startDate.toDateString() === endDate.toDateString()

    if (isSameDay) {
      testDateDisplay = formatReportDate(testStartDate)
    } else {
      testDateDisplay = `${formatReportDate(testStartDate)} to ${formatReportDate(testEndDate)}`
    }
  } else if (testStartDate) {
    testDateDisplay = formatReportDate(testStartDate)
  } else if (testEndDate) {
    testDateDisplay = formatReportDate(testEndDate)
  }

  const resultBody = [
    { data: section.toString(), colSpan: 1 },
    {
      data: `${examination.examinationConfig.name} \n \n${getNormName(examination.norm)}`,
      colSpan: 1,
    },
    { data: testerName, colSpan: 1 },
    { data: testDateDisplay, colSpan: 1 }, // Using date range display
    {
      data: getTestResultsForOverview()
        .map((item) => item.barcode)
        .join("\n"),
      colSpan: 1,
    },
    {
      data: getTestResultsForOverview()
        .map((item) => item.result)
        .join("\n"),
      colSpan: 1,
    },
  ]

  const resultsTable: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: true,
    tableCaption: "Results",
    tableNumber: createTableNumber(),
    tableHeader: [
      { data: "Sec.", colSpan: 1 },
      { data: "Test", colSpan: 1 },
      { data: "Responsible Person", colSpan: 1 },
      { data: "Date", colSpan: 1 },
      { data: "EUT No.", colSpan: 1 },
      { data: "Results", colSpan: 1 },
    ],
    tableBody: [...oldTableBody, [...resultBody]],
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  const textblock1: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "This test report may contain test methods which are not part of our accredited test areas. These tests are marked with an asterisk (\\*). Tests implemented in an accredited external laboratory are marked with two asterisks (\\*\\*). Tests implemented in a non-accredited external laboratory are marked with three asterisks (\\*\\*\\*).",
    editable: true,
    deletable: true,
    isAutoGenerated: true,
  }
  const textblock2: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "Text written in *italic type* is *external information*, not provided by TREO. This can be for example results or additional information provided by the client. The information is not part of results witnessed by TREO, hence TREO is not in charge for the content.",
    editable: true,
    deletable: true,
    isAutoGenerated: true,
  }

  const textblock3: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "Operating Mode (OM) described a specified setup and state of operation of one or more Equipment Under Test (EUT) and one or more Auxiliary Equipment (AE) as described in section 3 of this document.",
    editable: true,
    deletable: true,
    isAutoGenerated: true,
  }

  // Generate logistics table rows dynamically for each EUTGroup
  const generateLogisticsTableBody = () => {
    if (!eutGroups) {
      // Fallback to empty table if no EUTGroups provided
      return []
    }

    // Get EUT device IDs from ALL examinations in the report
    const allExaminationEutDeviceIds = (allExaminations || [examination])
      .flatMap((exam) => exam.deviceSetup)
      .flatMap((ds) => ds.device)
      .filter((device) => isDeviceEut(device))
      .map((device) => device.id)

    // Remove duplicates
    const uniqueEutDeviceIds = Array.from(new Set(allExaminationEutDeviceIds))

    // Find EUTGroups that contain devices used in ANY examination
    const relevantEutGroups = eutGroups.filter((eutGroup) =>
      eutGroup.devices.some((device) => uniqueEutDeviceIds.includes(device.id))
    )

    // Generate table rows for each relevant EUTGroup
    return relevantEutGroups.map((eutGroup) => [
      { data: `${eutGroup.barcode}-x - ${eutGroup.name} `, colSpan: 1 },
      { data: "", colSpan: 1 }, // Date column - empty for now
      { data: "", colSpan: 1 }, // Description column - empty for now
    ])
  }

  const logisticTable: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    editable: true,
    deletable: true,
    tableNumber: createTableNumber(),
    tableCaption: "Logistic",
    tableHeader: [
      { data: "EUT No.", colSpan: 1 },
      { data: "Date", colSpan: 1 },
      { data: "Description", colSpan: 1 },
    ],
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
    tableBody: generateLogisticsTableBody(),
  }

  return [resultsTable, textblock1, textblock2, textblock3, logisticTable]
}

// Create 3 Equipment Under Test Chapter
//   Create 3.1	Description of EUT
export const getEUT = (
  examination: Examination[],
  projektEutGroups: DeviceEutGroup[],
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  const eut2 = examination
    .flatMap((item) => item.deviceSetup.flatMap((ds) => ds.device))
    .filter((item) => isDeviceEut(item))
    .map((item) => item.id)

  const eut = projektEutGroups.filter((item) =>
    item.devices.some((device) => eut2.includes(device.id))
  )

  // 2. Generate new auto-generated nodes
  const nodes: DataNode[] = eut
    .map((item) => {
      // For EUT groups, we only need to show the specifications once,
      // not repeated for each device in the group
      const image: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.IMAGE,
        id: v4(),
        image: item.images.map((img) => img.id),
        imageCaption: item.images.map((img) => createCaption(img)),
        imageNumber: [],
        editable: true,
        deletable: true,
        isAutoGenerated: true,
        sourceExaminationId: undefined, // EUT data is not tied to a specific examination
      }

      // Show all device barcodes in the group
      const barcodes = item.devices.map((d) => d.barcode).join(", ")

      // Show count if multiple devices
      const deviceCount = item.devices.length
      const countText = deviceCount > 1 ? ` (${deviceCount} units)` : ""

      // Add device-specific fields - only include them if they have values
      const deviceFields: Array<
        [{ data: string; colSpan: number }, { data: string; colSpan: number }]
      > = []

      // Only add fields that have actual values
      if (item.name) {
        deviceFields.push([
          { data: "Name", colSpan: 1 },
          { data: item.name, colSpan: 1 },
        ])
      }
      if (item.manufacturer) {
        deviceFields.push([
          { data: "Manufacturer", colSpan: 1 },
          { data: item.manufacturer, colSpan: 1 },
        ])
      }
      if (item.model) {
        deviceFields.push([
          { data: "Model", colSpan: 1 },
          { data: item.model, colSpan: 1 },
        ])
      }
      if (item.serialNumber) {
        deviceFields.push([
          { data: "Serial number", colSpan: 1 },
          { data: item.serialNumber, colSpan: 1 },
        ])
      }
      if (item.function) {
        deviceFields.push([
          { data: "Function", colSpan: 1 },
          { data: item.function, colSpan: 1 },
        ])
      }
      if (item.productNumber) {
        deviceFields.push([
          { data: "Product number", colSpan: 1 },
          { data: item.productNumber, colSpan: 1 },
        ])
      }
      if (item.revision) {
        deviceFields.push([
          { data: "Revision", colSpan: 1 },
          { data: item.revision, colSpan: 1 },
        ])
      }
      if (item.software) {
        deviceFields.push([
          { data: "Software", colSpan: 1 },
          { data: item.software, colSpan: 1 },
        ])
      }
      if (item.softwareVersion) {
        deviceFields.push([
          { data: "Software Version", colSpan: 1 },
          { data: item.softwareVersion, colSpan: 1 },
        ])
      }

      if (item.description) {
        deviceFields.push([
          { data: "Description", colSpan: 1 },
          { data: item.description, colSpan: 1 },
        ])
      }

      // Add DeviceDataGroup fields
      const dataGroupFields = item.deviceDataGroup
        .flatMap((i) => i.deviceData)
        .map((d) => {
          if (d.data.length > 0) {
            const values = d.data
              .map((entry) => `${entry.value}${entry.unit ? ` ${entry.unit}` : ""}`.trim())
              .join(", ")
            return [
              { data: d.name, colSpan: 1 },
              { data: values, colSpan: 1 },
            ]
          }
          return []
        })
        .filter((row) => row.length > 0)

      // Combine both sets of fields
      const tableBody = [...deviceFields, ...dataGroupFields]

      const table: DataNode = {
        ...emptyDataNode,
        id: v4(),
        dataType: NodeDataType.TABLE,
        editable: true,
        deletable: true,
        tableCaption: "Description of " + item.name + countText,
        tableNumber: createTableNumber(),
        tableHeader: [
          { data: "Equipment No.", colSpan: 1 },
          { data: barcodes, colSpan: 1 },
        ],
        tableBody: tableBody,
        isAutoGenerated: true,
        sourceExaminationId: undefined, // EUT data is not tied to a specific examination
      }
      return [table, image]
    })
    .flat()

  // 3. Combine manual + auto-generated nodes with unique IDs
  return ensureUniqueNodeIds([...nodes, ...manualNodes])
}

//   Create 3.2	Description of Auxiliary Equipment (AE)
export const getEUTAuxiliary = (
  examinations: Examination[],
  devices: Device[],
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Collect all unique DeviceAux devices from all examinations
  const allAuxDevices = examinations.flatMap((examination) => {
    return examination.deviceSetup.flatMap((ds) => {
      if (isDeviceAux(ds.device)) return ds.device
      return []
    })
  })

  // 3. Remove duplicates based on device ID
  const uniqueAuxDevices = allAuxDevices.filter(
    (device, index, self) => index === self.findIndex((d) => d.id === device.id)
  )

  // 4. Generate table and image nodes for each unique AUX device
  const autoGeneratedNodes = uniqueAuxDevices.flatMap((item) => {
    // Cast to DeviceAux to access all fields
    const auxDevice = item as DeviceAux

    const auxDatas = devices.find((device) => device.id === auxDevice.id) as DeviceAux

    // Add device-specific fields - only include them if they have values
    const deviceFields: Array<
      [{ data: string; colSpan: number }, { data: string; colSpan: number }]
    > = []

    // Only add fields that have actual values

    if (auxDatas.name) {
      deviceFields.push([
        { data: "Name", colSpan: 1 },
        { data: auxDatas.name, colSpan: 1 },
      ])
    }
    if (auxDatas.manufacturer) {
      deviceFields.push([
        { data: "Manufacturer", colSpan: 1 },
        { data: auxDatas.manufacturer, colSpan: 1 },
      ])
    }
    if (auxDatas.model) {
      deviceFields.push([
        { data: "Model", colSpan: 1 },
        { data: auxDatas.model, colSpan: 1 },
      ])
    }
    if (auxDatas.serialNumber) {
      deviceFields.push([
        { data: "Serial number", colSpan: 1 },
        { data: auxDatas.serialNumber, colSpan: 1 },
      ])
    }
    if (auxDatas.function) {
      deviceFields.push([
        { data: "Function", colSpan: 1 },
        { data: auxDatas.function, colSpan: 1 },
      ])
    }

    // Add DeviceDataGroup fields
    const dataGroupFields = auxDatas.deviceDataGroup
      .flatMap((i: DeviceDataGroup) => i.deviceData)
      .map((d: DeviceData) => {
        if (d.data.length > 0) {
          const values = d.data
            .map((entry) => `${entry.value}${entry.unit ? ` ${entry.unit}` : ""}`.trim())
            .join(", ")
          return [
            { data: d.name, colSpan: 1 },
            { data: values, colSpan: 1 },
          ]
        }
        return []
      })
      .filter((row: any) => row.length > 0)

    // Combine both sets of fields
    const tableBody = [...deviceFields, ...dataGroupFields]

    const table: DataNode = {
      ...emptyDataNode,
      id: v4(),
      dataType: NodeDataType.TABLE,
      editable: true,
      deletable: true,
      tableCaption: "Description of " + auxDevice.barcode,
      tableNumber: createTableNumber(),
      tableHeader: [
        { data: "Equipment No.", colSpan: 1 },
        { data: auxDevice.barcode, colSpan: 1 },
      ],
      tableBody: tableBody,
      isAutoGenerated: true,
      sourceExaminationId: undefined, // AUX data is not tied to a specific examination
    }

    // Add images if they exist
    const result: DataNode[] = [table]
    if (auxDatas.images && auxDatas.images.length > 0) {
      const image: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.IMAGE,
        id: v4(),
        image: auxDatas.images.map((img) => img.id),
        imageCaption: auxDatas.images.map((img) => createCaption(img)),
        imageNumber: [],
        editable: true,
        deletable: true,
        isAutoGenerated: true,
        sourceExaminationId: undefined, // AUX data is not tied to a specific examination
      }
      result.push(image)
    }

    return result
  })

  // 3. Combine manual + auto-generated nodes with unique IDs
  return ensureUniqueNodeIds([...autoGeneratedNodes, ...manualNodes])
}

//   Create 3.3	Entrance Examination
export const getEUTEntranceExamination = (existingData: DataNode[] = []): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes

  const introText: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: "The test equipment was checked after arrival by visual inspection. No external damage was detected.",
    editable: true,
    deletable: true,
    isAutoGenerated: true,
    sourceExaminationId: undefined, // Entrance examination is not tied to a specific examination
  }

  const autoGeneratedNodes = [introText]
  return [...autoGeneratedNodes, ...manualNodes]
}
//   Create 3.4	Description of Connections
//   Create 3.5	Description of Functional Checks

// Create 4.1.1 Adapted Standard
export const getAdaptedStandard = (
  examination: Examination,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes

  const text: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.TEXT,
    editable: true,
    deletable: true,
    text: `${getNormName(examination.norm)}`,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  // 3. Combine manual + auto-generated nodes
  return [...manualNodes, text]
}

// Create 4.1.2 Qualification Test Plan

// Create 4.1.3 Test Parameter / Category
export const getTestParametersAndCategory = (
  examination: Examination,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes
  const autoGeneratedNodes = examination.testParameterBlock.flatMap((item) =>
    item.block.map((block) => ({
      ...block,
      isAutoGenerated: true,
      sourceExaminationId: examination.id,
    }))
  )

  // 3. Combine manual + auto-generated nodes
  return [...autoGeneratedNodes, ...manualNodes]
}

// Create 4.1.4 Deviations

// Create 4.1.5 Remarks

// Create 4.1.6 Pass / Fail Criteria
export const getPassFailCriteria = (
  examination: Examination,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes

  const autogeneratedNodes: DataNode[] = examination.passFailCriteria
    .map((cc) => {
      // Pass/Fail Criteria Name
      const nameBlock: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.TEXT,
        id: v4(),
        text: `**${cc.name}**`,
        editable: true,
        deletable: true,
      }

      // Pass/Fail Criteria Description
      const descriptionBlock: DataNode[] = cc.description

      // Zwischenüberschrift
      const betweenHeaderBlock: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.TEXT,
        id: v4(),
        text: "**Kriterienliste**",
        editable: true,
        deletable: true,
      }

      // Pass/Fail Criteria Liste
      const criteriaListBlock = cc.criteriaList
        .map((item) => {
          const criteriaNameBlock: DataNode = {
            ...emptyDataNode,
            dataType: NodeDataType.TEXT,
            id: v4(),
            text: `- ${item.name} for operating modes: ${item.operatingModes.join(", ")}`,
            editable: true,
            deletable: true,
          }

          return [criteriaNameBlock, ...item.description]
        })
        .flat()

      return [nameBlock, ...descriptionBlock, betweenHeaderBlock, ...criteriaListBlock]
    })
    .flat()

  return [...autogeneratedNodes, ...manualNodes]
}

// Create 4.2 Test Location and Conditions
export const getTestLocationAndConditions = (
  examination: Examination,
  report?: Report,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes
  const datas = examination.tasks
    .flatMap((task) => task.testDatas)
    .filter((test) => setUpTasks.includes(test.type))

  const ambienteConditions = datas.flatMap((item) => item.ambientConditions)

  const newData: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.TABLE,
    editable: true,
    deletable: true,
    tableCaption: "Limits of Ambient Conditions",
    tableHeader: [
      { data: "Temperature [°C]", colSpan: 1 },
      { data: "Ambient Pressure abs.[mbar]", colSpan: 1 },
      { data: "Relative Humidity [% RH]", colSpan: 1 },
    ],
    tableBody: [
      [
        { data: "+15 to +35", colSpan: 1 },
        { data: "860 to 1060", colSpan: 1 },
        { data: "30 to 75", colSpan: 1 },
      ],
    ],
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  const ambientConditionsNotPassed: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.TEXT,
    editable: true,
    deletable: true,
    text: "The ambient conditions were not met",
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  // Get actual test facility from report
  const testFacility = formatMissingValue(examination.location.name)

  const location: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.TEXT,
    editable: true,
    deletable: true,
    text: `Test Facility: ${testFacility}`,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }
  // 3. Combine manual + auto-generated nodes
  const autoGeneratedNodes =
    datas.length > 0
      ? ambienteConditions
        ? [location, newData]
        : [location, ambientConditionsNotPassed]
      : []

  return [...autoGeneratedNodes, ...manualNodes]
}

// Create 4.3 TestEquipment Chapter
export const getTestEquipment = (
  examination: Examination,
  projectTestEquipment: DeviceTestEquipment[],
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes
  const scannedTestEquipment: DeviceTestEquipment[] = examination.deviceSetup.flatMap((ds) => {
    if (isDeviceTe(ds.device)) return ds.device
    if (ds.linkedTo)
      return ds.linkedTo
        .map((item) => (item && isDeviceTe(item.device) ? item.device : null))
        .filter((device): device is DeviceTestEquipment => device !== null)
    return []
  }) as DeviceTestEquipment[]

  const te = scannedTestEquipment
    .map((ste) => {
      const returnValue = projectTestEquipment.find((item) => ste.id === item.id)
      return returnValue ? returnValue : undefined
    })
    .filter((item): item is DeviceTestEquipment => item !== undefined)

  if (te.length === 0) return []

  const tableBody = te.map((item: DeviceTestEquipment) => [
    { data: item.name || "", colSpan: 1 },
    { data: item.model || item.teType || "", colSpan: 1 },
    { data: item.manufacturer || "", colSpan: 1 },
    { data: item.inventoryNo || "", colSpan: 1 },
    { data: item.calibrationDue || "", colSpan: 1 },
  ])

  const tableHeader = [
    { data: "Name", colSpan: 1 },
    { data: "Type", colSpan: 1 },
    { data: "Manufacturer", colSpan: 1 },
    { data: "Inventory No.", colSpan: 1 },
    { data: "Calibration Due", colSpan: 1 },
  ]

  const table: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TABLE,
    id: v4(),
    tableCaption: "Test Equipment",
    tableNumber: createTableNumber(),
    tableHeader: tableHeader,
    tableBody: tableBody,
    editable: true,
    deletable: true,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  const uncertainties = getMaxValuesByUnit(te.flatMap((item) => item.uncertainty)).join(", ")

  const text: DataNode = {
    ...emptyDataNode,
    dataType: NodeDataType.TEXT,
    id: v4(),
    text: `Expanded measurement uncertainty: ${uncertainties}.`,
    editable: true,
    deletable: true,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  // 3. Combine manual + auto-generated nodes
  const autoGeneratedNodes = te.length > 0 ? [table, text] : []
  return [...autoGeneratedNodes, ...manualNodes]
}

// Create 4.4 Witnesses Chapter
export const getWitnesses = (
  examination: Examination,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes
  // Get witnesses from tasks (witnesses are on TestData, not on Examination)
  const taskWitnesses = examination.tasks.flatMap((task) =>
    task.testDatas.flatMap((data) => data.witnesses)
  )

  // Deduplicate witnesses
  const uniqueWitnesses = taskWitnesses.filter(
    (witness, index, self) =>
      index ===
      self.findIndex(
        (w) => w?.firstName === witness?.firstName && w?.lastName === witness?.lastName
      )
  )

  const tableBody = uniqueWitnesses.map((item) => [
    { data: `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || "Unknown", colSpan: 1 },
    { data: `${item?.companyName || ""}`.trim() || "Unknown", colSpan: 1 },
  ])
  const newData: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.TABLE,
    tableCaption: "External Witnessing Persons",
    tableNumber: createTableNumber(),
    tableHeader: [
      { data: "Name", colSpan: 1 },
      { data: "Company", colSpan: 1 },
    ],
    tableBody: tableBody,
    editable: true,
    deletable: true,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }
  // 3. Combine manual + auto-generated nodes
  const autoGeneratedNodes = uniqueWitnesses.length > 0 ? [newData] : []
  return [...autoGeneratedNodes, ...manualNodes]
}

// Create 4.5 Test Setup Chapter
export const getTestSetup = (
  examination: Examination,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes
  const datas = examination.tasks
    .flatMap((task) => task.testDatas)
    .filter((test) => setUpTasks.includes(test.type))

  const images = datas.flatMap((item) => item.image)

  const deviceHasPassedImages = examination.deviceSetup
    .filter((item) => isDeviceEut(item.device))
    .flatMap((item) => item.images)

  const newData: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.IMAGE,
    image: images.map((img) => img.id),
    imageCaption: images.map((img) => createCaption(img)),
    imageNumber: createImageNumbers(images.length),
    editable: true,
    deletable: true,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  const newDataDevice: DataNode = {
    ...emptyDataNode,
    id: v4(),
    dataType: NodeDataType.IMAGE,
    image: deviceHasPassedImages.map((img) => img.id),
    imageCaption: deviceHasPassedImages.map((img) => createCaption(img)),
    imageNumber: createImageNumbers(images.length),
    editable: true,
    deletable: true,
    isAutoGenerated: true,
    sourceExaminationId: examination.id,
  }

  const returnValue = (): DataNode[] => {
    if (images.length > 0 && deviceHasPassedImages.length > 0) {
      return [newData, newDataDevice]
    }
    if (images.length > 0) {
      return [newData]
    }
    if (deviceHasPassedImages.length > 0) {
      return [newDataDevice]
    }
    return []
  }

  // 3. Combine manual + auto-generated nodes
  const autoGeneratedNodes = returnValue()
  return [...autoGeneratedNodes, ...manualNodes]
}

// Create 4.6 Results
export const getTestResults = (
  examination: Examination,
  existingData: DataNode[] = []
): DataNode[] => {
  // 1. Keep all manually added nodes (not auto-generated)
  const manualNodes = existingData.filter((node) => !node.isAutoGenerated)

  // 2. Generate new auto-generated nodes
  const resultTestDatas = examination.tasks
    .flatMap((task) => task.testDatas)
    .find((data) => resultTasks.includes(data.type))

  if (resultTestDatas) {
    const nodes: DataNode[] = []

    const tableBody = resultTestDatas.deviceTestData.map((item) => {
      const testResult = getTestResultsForTable(item)
      return [
        { data: item.device.barcode, colSpan: 1 },
        { data: testResult.result, colSpan: 1 },
        {
          data: item.note === "" ? "./." : item.note,
          colSpan: 1,
        },
      ]
    })

    const newData: DataNode = {
      ...emptyDataNode,
      id: v4(),
      dataType: NodeDataType.TABLE,
      tableCaption: "Overview of Results",
      tableNumber: createTableNumber(),
      tableHeader: [
        { data: "EUT No.", colSpan: 1 },
        { data: "Result", colSpan: 1 },
        { data: "Comment", colSpan: 1 },
      ],
      tableBody: tableBody,
      editable: true,
      deletable: true,
      isAutoGenerated: true,
      sourceExaminationId: examination.id,
    }
    nodes.push(newData)

    // Add result images/diagrams if available
    const resultImages = resultTestDatas.image || []
    const deviceImages = resultTestDatas.deviceTestData
      .flatMap((item) => item.images || [])
      .filter((img) => img !== null)

    const allImages = [...resultImages, ...deviceImages]

    if (allImages.length > 0) {
      const imageNode: DataNode = {
        ...emptyDataNode,
        dataType: NodeDataType.IMAGE,
        id: v4(),
        image: allImages.map((img) => img.id),
        imageCaption: allImages.map((img) => createCaption(img)),
        imageNumber: createImageNumbers(allImages.length),
        editable: true,
        deletable: true,
        isAutoGenerated: true,
        sourceExaminationId: examination.id,
      }
      nodes.push(imageNode)
    }

    // 3. Combine manual + auto-generated nodes
    return [...manualNodes, ...nodes]
  }

  return manualNodes
}

export const getEmptyChapter = () => {
  return [
    {
      ...emptyDataNode,
      dataType: NodeDataType.TEXT,
      id: v4(),
      text: "./.",
    },
  ]
}
